import React, { useEffect, useState, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { AuthContext } from '../App';
import { Logo } from '../components/EnergyField';
import { readTime, statusTag, difficultyTag, isExperiment, classifyReference } from '../lib/meta';
import { EvidenceLabel, ValueLabel } from '../components/EvidenceLabel';
import { evidenceMeta, relationshipMeta } from '../lib/evidence';
import { ArchiveDepth } from '../components/ArchiveDepth';
import { domainImage } from '../lib/domainImages';
import {
  ArrowLeft, ArrowRight, ArrowUpRight, Clock, GitBranch, Link2, Layers,
  FileText, FlaskConical, BookOpen, ChevronRight, ExternalLink, Compass, Lock,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const renderContent = (content) => {
  const parts = (content || '').split('```');
  return parts.map((part, i) =>
    i % 2 === 1 ? (
      <pre key={i} className="my-7 glass-panel rounded-lg p-4 overflow-x-auto font-mono-tech text-xs text-[#9FB0AD] leading-relaxed whitespace-pre border-l-2 border-l-[#1E8F89]/50">{part.replace(/^\n+|\n+$/g, '')}</pre>
    ) : (
      <div key={i} className="font-sans-tech text-[#C8CDD6] leading-[1.95] whitespace-pre-wrap">{part}</div>
    )
  );
};

const MetaRow = ({ label, children }) => (
  <div className="flex items-start justify-between gap-6 py-2.5 border-b border-[#262A36]/60 last:border-0">
    <span className="mono-meta shrink-0 pt-0.5">{label}</span>
    <span className="text-right font-sans-tech text-sm text-[#C8CDD6]">{children}</span>
  </div>
);

const CrossSection = ({ title, Icon, children, testid }) => (
  <div data-testid={testid}>
    <p className="mono-label mb-3 flex items-center gap-2"><Icon className="w-3.5 h-3.5" /> {title}</p>
    <div className="glass-panel rounded-xl px-5">{children}</div>
  </div>
);

const CrossRow = ({ onClick, Icon, label, meta, testid }) => (
  <button onClick={onClick} className="w-full text-left py-3.5 border-b border-[#262A36]/60 last:border-0 group flex items-center justify-between gap-4" data-testid={testid}>
    <span className="flex items-center gap-3 min-w-0">
      <Icon className="w-4 h-4 text-[#6B7280] group-hover:text-[#26B0A8] transition-colors shrink-0" />
      <span className="font-sans-tech text-sm text-[#C8CDD6] group-hover:text-[#26B0A8] transition-colors truncate">{label}</span>
    </span>
    <span className="flex items-center gap-2 shrink-0">
      <span className="mono-meta">{meta}</span>
      <ArrowUpRight className="w-3.5 h-3.5 text-[#3a4254] group-hover:text-[#26B0A8] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
    </span>
  </button>
);

const ArticleView = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useContext(AuthContext);
  const [article, setArticle] = useState(null);
  const [allArticles, setAllArticles] = useState([]);
  const [allPdfs, setAllPdfs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [stats, setStats] = useState(null);
  const [relatedLinks, setRelatedLinks] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadAll = async () => {
    try {
      const [art, list, pdfs, cats, st, rel] = await Promise.all([
        axios.get(`${API}/public/articles/${id}`),
        axios.get(`${API}/public/articles`),
        axios.get(`${API}/public/pdfs`),
        axios.get(`${API}/categories`),
        axios.get(`${API}/public/stats`),
        axios.get(`${API}/public/articles/${id}/related`),
      ]);
      setArticle(art.data);
      setAllArticles(list.data);
      setAllPdfs(pdfs.data);
      setCategories(cats.data);
      setStats(st.data);
      setRelatedLinks(rel.data || []);
    } catch (error) {
      if (error.response?.status === 404) setArticle(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void Promise.resolve().then(() => { setLoading(true); loadAll(); });
    window.scrollTo(0, 0);
  }, [id, user]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-[#08080A]"><div className="spinner"></div></div>;
  }

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#08080A]">
        <div className="text-center space-y-6">
          <p className="font-sans-tech text-[#949BAA]">Research log not found</p>
          <button onClick={() => navigate(user ? '/dashboard' : '/')} className="btn-quiet">Back to Interface</button>
        </div>
      </div>
    );
  }

  const status = statusTag(article.status);
  const diff = difficultyTag(article.difficulty);
  const refs = article.references || [];
  const related = article.related_domains || [];
  const catName = (cid) => categories.find((c) => c.id === cid)?.name || '';
  const domainName = catName(article.category_id);
  const goDomain = (nameOrId) => navigate(`/dashboard?domain=${encodeURIComponent(nameOrId)}`);

  const sameDomain = (x) => x.category_id === article.category_id;
  const inRelated = (x) => related.includes(catName(x.category_id));

  const relatedLogs = allArticles
    .filter((a) => a.id !== article.id && !isExperiment(a) && (sameDomain(a) || inRelated(a)))
    .slice(0, 4);
  const referencedExperiments = allArticles
    .filter((a) => a.id !== article.id && isExperiment(a) && (sameDomain(a) || inRelated(a)))
    .slice(0, 3);
  const connectedBlueprints = allPdfs
    .filter((p) => sameDomain(p) || inRelated(p))
    .slice(0, 3);

  return (
    <div className="min-h-screen bg-[#08080A] text-[#E2E4E9] overflow-x-hidden">
      <nav className="sticky top-0 z-50 glass-hud border-b border-[#262A36]">
        <div className="container mx-auto px-6 lg:px-12 py-4 flex justify-between items-center gap-4">
          <button onClick={() => navigate('/')} className="shrink-0"><Logo /></button>
          <button onClick={() => navigate(user ? '/dashboard' : '/')} className="btn-quiet !py-2 !px-5 shrink-0" data-testid="back-button"><ArrowLeft className="w-4 h-4" /> {user ? 'Back to Interface' : 'Back to Archive'}</button>
        </div>
      </nav>

      <div className="relative w-full h-56 lg:h-72 overflow-hidden border-b border-[#262A36]" data-testid="article-hero">
        <img src={domainImage(domainName)} alt={domainName || 'Field research'} className="w-full h-full object-cover opacity-[0.55] grayscale-[0.25]" loading="eager" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#08080A]/40 via-[#08080A]/30 to-[#08080A]" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#08080A]/70 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0">
          <div className="container mx-auto px-6 lg:px-12 pb-6 max-w-4xl">
            <p className="mono-meta text-[#26B0A8] flex items-center gap-2"><span className="weekly-dot !w-1.5 !h-1.5" /> {domainName || 'Field Research'} · Archive Record</p>
          </div>
        </div>
      </div>

      <motion.article initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="container mx-auto px-6 lg:px-12 py-12 max-w-2xl" data-testid="article-content">
        {/* Archive Trail */}
        <div className="flex items-center gap-2 mb-7 flex-wrap" data-testid="archive-trail">
          <button onClick={() => navigate('/dashboard')} className="mono-meta hover:text-[#26B0A8] transition-colors">Archive</button>
          <ChevronRight className="w-3 h-3 text-[#3a4254]" />
          <button onClick={() => goDomain(domainName)} className="mono-meta hover:text-[#26B0A8] transition-colors" data-testid="trail-domain">{domainName || 'Domain'}</button>
          <ChevronRight className="w-3 h-3 text-[#3a4254]" />
          <span className="mono-meta text-[#1E8F89]">{article.content_type || 'Log'}</span>
        </div>

        {/* Read time hidden for locked records */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <span className={`tag ${status.cls}`}>{status.label}</span>
          {!article.locked && <span className="mono-meta flex items-center gap-1.5"><Clock className="w-3 h-3" /> {readTime(article.content)} min read</span>}
          {article.locked && <span className="tag tag-muted flex items-center gap-1"><Lock className="w-3 h-3" /> Restricted</span>}
          <span className="mono-meta">{new Date(article.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
        </div>
        <h1 className="font-serif-display text-4xl lg:text-6xl font-light mb-6 leading-[1.08]">{article.title}</h1>
        <p className="font-sans-tech text-lg text-[#949BAA] mb-8 leading-relaxed">{article.description}</p>

        {/* Metadata panel */}
        <div className="glass-panel rounded-xl px-6 py-4 mb-10" data-testid="article-metadata">
          <MetaRow label="Type">{article.content_type || '—'}</MetaRow>
          {article.content_value_label && <MetaRow label="Work"><ValueLabel label={article.content_value_label} testid="article-value-label" /></MetaRow>}
          {article.evidence_class && (
            <MetaRow label="Evidence">
              <span className="inline-flex flex-col items-end gap-1">
                <EvidenceLabel evidenceClass={article.evidence_class} testid="article-evidence" />
                <span className="mono-meta text-[#5b626f] max-w-[16rem]">{evidenceMeta(article.evidence_class)?.note}</span>
              </span>
            </MetaRow>
          )}
          <MetaRow label="Status"><span className={`tag ${status.cls}`}>{status.label}</span></MetaRow>
          <MetaRow label="Classification"><span className={`tag ${diff.cls}`}>{diff.label}</span></MetaRow>
          {article.experiment_phase && <MetaRow label="Phase"><span className="flex items-center gap-1.5 justify-end"><GitBranch className="w-3.5 h-3.5 text-[#6B7280]" /> {article.experiment_phase}</span></MetaRow>}
          <MetaRow label="Updated">{new Date(article.updated_at || article.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</MetaRow>
          {related.length > 0 && (
            <MetaRow label="Adjacent">
              <span className="flex flex-wrap gap-1.5 justify-end" data-testid="adjacent-domains">
                {related.map((r) => (
                  <button key={r} onClick={() => goDomain(r)} className="tag tag-steel flex items-center gap-1 hover:border-[#1E8F89]/60 hover:text-[#26B0A8] transition-colors" data-testid={`adjacent-${r.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
                    <Layers className="w-3 h-3" /> {r}
                  </button>
                ))}
              </span>
            </MetaRow>
          )}
        </div>

        {article.weekly_open && (!user || user.subscription_status !== 'active') && (
          <div className="mb-8 flex items-center gap-3 rounded-xl border border-[#1E8F89]/25 bg-[#1E8F89]/[0.04] px-5 py-3.5" data-testid="weekly-banner">
            <span className="weekly-dot" />
            <p className="mono-meta text-[#26B0A8]">Temporarily public · Released from the subscriber archive{article.weekly_opened_at ? ` · surfaced ${new Date(article.weekly_opened_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` : ''}</p>
          </div>
        )}

        {article.locked ? (
          <div className="border-t border-[#262A36] pt-10" data-testid="locked-article">
            {article.excerpt && (
              <div className="relative mb-6 max-h-44 overflow-hidden">
                <p className="font-sans-tech text-[#9aa3b2] leading-[1.95] blur-[2.5px] select-none">{article.excerpt}</p>
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#08080A]/50 to-[#08080A]" />
              </div>
            )}
            <div className="glass-panel rounded-xl px-6 py-5 mb-8" data-testid="redacted-sections">
              <p className="mono-label mb-4 flex items-center gap-2 text-[#6B7280]"><Lock className="w-3 h-3" /> Restricted Sections · Subscriber Archive</p>
              <div className="space-y-1">
                {['Full field log', 'Measurement data tables', 'Field references & sources', 'Prototype build guide'].map((s, i) => (
                  <div key={s} className="flex items-center justify-between gap-6 py-2 border-b border-[#262A36]/50 last:border-0">
                    <span className="font-sans-tech text-sm text-[#6B7280] whitespace-nowrap">{s}</span>
                    <span className="redact" style={{ width: `${5 + (i % 3) * 1.4}rem` }} aria-hidden="true" />
                  </div>
                ))}
              </div>
            </div>
            <div className="glass-hud rounded-2xl p-8 lg:p-10 text-center reflect-top scanlines">
              <div className="w-14 h-14 rounded-xl border border-[#1E8F89]/40 bg-[#1E8F89]/10 flex items-center justify-center mx-auto mb-6"><Lock className="w-6 h-6 text-[#26B0A8]" /></div>
              <p className="mono-label mb-3">Restricted Archive Record</p>
              <h3 className="font-serif-display text-3xl font-light mb-4">Research Access Required</h3>
              <p className="font-sans-tech text-[#949BAA] mb-6 max-w-md mx-auto">This record sits inside the full research network. Unlock the complete archive, blueprints, and experiments with Research Access — €9 / month.</p>
              <ArchiveDepth stats={stats} mode="unlock" className="justify-center mb-7" testid="article-depth" />
              <button onClick={() => navigate(user ? '/dashboard' : '/')} className="btn-primary" data-testid="article-unlock-button">{user ? 'Unlock Research Access' : 'Enter Research Lab'} <ArrowRight className="w-4 h-4" /></button>
            </div>
          </div>
        ) : (
          <div className="border-t border-[#262A36] pt-10">
            {renderContent(article.content)}
          </div>
        )}

        {/* References */}
        {!article.locked && refs.length > 0 && (
          <div className="mt-12 border-t border-[#262A36] pt-8" data-testid="article-references">
            <p className="mono-label mb-4 flex items-center gap-2"><Link2 className="w-3.5 h-3.5" /> Field References</p>
            <ul className="space-y-1">
              {refs.map((r, i) => {
                const c = classifyReference(r, allArticles);
                if (c.kind === 'internal') {
                  return (
                    <li key={i}>
                      <button onClick={() => navigate(`/article/${c.article.id}`)} className="w-full text-left py-2.5 flex items-start gap-3 group" data-testid={`ref-internal-${i}`}>
                        <span className="mono-meta pt-0.5">[{i + 1}]</span>
                        <span className="font-sans-tech text-sm text-[#C8CDD6] group-hover:text-[#26B0A8] transition-colors">{r}</span>
                        <span className="tag tag-teal ml-auto shrink-0 flex items-center gap-1"><BookOpen className="w-3 h-3" /> Archive record</span>
                      </button>
                    </li>
                  );
                }
                if (c.kind === 'external') {
                  return (
                    <li key={i}>
                      <a href={r} target="_blank" rel="noopener noreferrer" className="py-2.5 flex items-start gap-3 group" data-testid={`ref-external-${i}`}>
                        <span className="mono-meta pt-0.5">[{i + 1}]</span>
                        <span className="font-sans-tech text-sm text-[#949BAA] group-hover:text-[#26B0A8] transition-colors break-all">{r}</span>
                        <span className="tag tag-muted ml-auto shrink-0 flex items-center gap-1"><ExternalLink className="w-3 h-3" /> External</span>
                      </a>
                    </li>
                  );
                }
                return (
                  <li key={i} className="py-2.5 flex items-start gap-3" data-testid={`ref-source-${i}`}>
                    <span className="mono-meta pt-0.5">[{i + 1}]</span>
                    <span className="font-sans-tech text-sm text-[#949BAA]">{r}</span>
                    <span className="tag tag-muted ml-auto shrink-0">Source material</span>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </motion.article>

      {/* CONTINUE EXPLORING — cross-linked network */}
      {(relatedLinks.length > 0 || relatedLogs.length > 0 || referencedExperiments.length > 0 || connectedBlueprints.length > 0) && (
        <section className="border-t border-[#262A36]" data-testid="continue-exploring">
          <div className="container mx-auto px-6 lg:px-12 py-14 max-w-5xl">
            <p className="mono-label mb-2 flex items-center gap-2"><Compass className="w-3.5 h-3.5" /> Archive Trail</p>
            <h2 className="font-serif-display text-3xl lg:text-4xl font-light mb-10">Continue Exploring</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {relatedLinks.length > 0 && (
                <CrossSection title="Connected Records" Icon={GitBranch} testid="connected-records">
                  {relatedLinks.map((r) => {
                    const RIcon = r.type === 'pdf' ? FileText : Link2;
                    const rel = relationshipMeta(r.relationship_type);
                    return (
                      <button
                        key={r.id}
                        onClick={() => navigate(r.type === 'pdf' ? '/dashboard?tab=vault' : `/article/${r.id}`)}
                        className="w-full text-left py-3.5 border-b border-[#262A36]/60 last:border-0 group flex items-start gap-3"
                        data-testid={`connected-record-${r.id}`}
                      >
                        <RIcon className="w-4 h-4 text-[#6B7280] group-hover:text-[#26B0A8] transition-colors shrink-0 mt-0.5" />
                        <span className="min-w-0 flex-1">
                          <span className="block font-sans-tech text-sm text-[#C8CDD6] group-hover:text-[#26B0A8] transition-colors truncate">{r.title}</span>
                          <span className="flex items-center gap-2 flex-wrap mt-0.5">
                            <span className="mono-meta text-[#26B0A8]" title={rel.note}>{rel.label}</span>
                            {typeof r.relationship_confidence === 'number' && <span className="mono-meta text-[#5b626f]">· confidence {r.relationship_confidence}/100</span>}
                            {r.evidence_class && <EvidenceLabel evidenceClass={r.evidence_class} />}
                          </span>
                          {r.relationship_basis && <span className="block mono-meta text-[#5b626f] mt-1 leading-relaxed">{r.relationship_basis}</span>}
                        </span>
                        <ArrowUpRight className="w-3.5 h-3.5 text-[#3a4254] group-hover:text-[#26B0A8] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all shrink-0 mt-0.5" />
                      </button>
                    );
                  })}
                  <p className="mono-meta text-[#5b626f] py-3 leading-relaxed border-t border-[#262A36]/60">
                    Connections identify research relationships, not verified scientific truth. <button onClick={() => navigate('/method')} className="text-[#26B0A8] hover:text-[#34d3ca] transition-colors" data-testid="connected-method-link">Method →</button>
                  </p>
                </CrossSection>
              )}
              {relatedLogs.length > 0 && (
                <CrossSection title="Related Field Logs" Icon={BookOpen} testid="related-logs">
                  {relatedLogs.map((a) => (
                    <CrossRow key={a.id} Icon={BookOpen} label={a.title} meta={`${readTime(a.content)} min`} onClick={() => navigate(`/article/${a.id}`)} testid={`related-log-${a.id}`} />
                  ))}
                </CrossSection>
              )}
              {referencedExperiments.length > 0 && (
                <CrossSection title="Referenced Experiments" Icon={FlaskConical} testid="referenced-experiments">
                  {referencedExperiments.map((a) => (
                    <CrossRow key={a.id} Icon={FlaskConical} label={a.title} meta={statusTag(a.status).label} onClick={() => navigate(`/article/${a.id}`)} testid={`ref-exp-${a.id}`} />
                  ))}
                </CrossSection>
              )}
              {connectedBlueprints.length > 0 && (
                <CrossSection title="Connected Blueprints" Icon={FileText} testid="connected-blueprints">
                  {connectedBlueprints.map((p) => (
                    <CrossRow key={p.id} Icon={FileText} label={p.title} meta={p.file_size || 'PDF'} onClick={() => navigate('/dashboard?tab=vault')} testid={`connected-bp-${p.id}`} />
                  ))}
                </CrossSection>
              )}
              <CrossSection title="Adjacent Domains" Icon={Layers} testid="adjacent-section">
                <CrossRow Icon={Compass} label={`Explore ${domainName} domain`} meta="Filter" onClick={() => goDomain(domainName)} testid="explore-domain" />
                {related.map((r) => (
                  <CrossRow key={r} Icon={Layers} label={r} meta="Domain" onClick={() => goDomain(r)} testid={`adjacent-row-${r.toLowerCase().replace(/[^a-z]+/g, '-')}`} />
                ))}
                <CrossRow Icon={FileText} label="Open Blueprint Vault" meta="Archive" onClick={() => navigate('/dashboard?tab=vault')} testid="open-vault" />
                <CrossRow Icon={ArrowRight} label="Latest Field Updates" meta="Transmissions" onClick={() => navigate('/dashboard?tab=updates')} testid="open-updates" />
              </CrossSection>
            </div>
          </div>
        </section>
      )}

      <div className="border-t border-[#262A36]">
        <div className="container mx-auto px-6 lg:px-12 py-10">
          <button onClick={() => navigate(user ? '/dashboard' : '/')} className="btn-ambient" data-testid="back-to-lab-button"><ArrowLeft className="w-4 h-4" /> {user ? 'Back to Research Interface' : 'Back to Archive'}</button>
        </div>
      </div>
    </div>
  );
};

export default ArticleView;
